import {
  useCallback,
  Suspense,
  useSyncExternalStore,
  useMemo,
  useState,
  useId,
  useRef,
} from "react";
import { Play, RotateCcw, Loader2, CheckCircle, XCircle } from "lucide-react";
import { Button } from "../Button";
import { LanguageIcon, hasLanguageIcon } from "../Icon";
import { lazyNamed } from "../../../lib/lazy-named";

/*
素の lazy + .then で名前付き export を取り出さない。

Vite の preload ヘルパーは vite:preloadError を preventDefault されると
例外を投げずに終わるので、import() が undefined で解決する。entry.client は
まさにそれをして読み直しをかけるため、読み直しが始まるまでの数十ミリ秒に
mod.CodeEditor を読んで落ちる。本当の原因はチャンクの 404 なのに、Sentry には
"Cannot read properties of undefined (reading 'CodeEditor')" だけが並ぶ
(2026-09-03、Short ID PROD-CHOTDEKIRU-WEB-3N)
*/
const CodeEditor = lazyNamed(
  () => import("../../domain/CodeEditor/CodeEditor"),
  (mod) => mod.CodeEditor,
);

/*
色は --rcb-* の変数で持つ。明るい版と暗い版の値はどちらも apps/client/app/app.css の [data-runnable-code-block] に置き
(値は tokens/colors.ts)、同じ所の @theme inline で bg-rcb-paper のような短い class にしてある。記事に欄が何十個も並ぶので、値を欄ごとの class に書くと SSR の HTML が欄の数だけ重くなる。
*/
const RCB = {
  paper: "bg-rcb-paper",
  band: "bg-rcb-band",
  edge: "border-rcb-edge",
  ink: "text-rcb-ink",
  sub: "text-rcb-sub",
  btn: "bg-rcb-btn",
  btnHover: "hover:bg-rcb-btn-hover",
  link: "text-rcb-link",
  badgeBg: "bg-rcb-badge-bg",
} as const;

/** 暗い版かどうか。html の data-theme か、近くの data-lg-mode (/luagate の器) を見る */
function readIsDark(el: Element | null): boolean {
  const lg = el?.closest("[data-lg-mode]")?.getAttribute("data-lg-mode");
  if (lg) return lg === "dark";
  return document.documentElement.getAttribute("data-theme") === "dark";
}

function useIsDark(ref: { current: Element | null }): boolean {
  const subscribe = useCallback((onChange: () => void) => {
    const observer = new MutationObserver(onChange);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    const lg = ref.current?.closest("[data-lg-mode]");
    if (lg) observer.observe(lg, { attributes: true, attributeFilter: ["data-lg-mode"] });
    return () => observer.disconnect();
  }, [ref]);
  return useSyncExternalStore(subscribe, () => readIsDark(ref.current), () => false);
}

export interface CodeExecutionResult {
  output: string;
  error: string | null;
  image?: string | null;
}

// グローバル状態ストア（コンポーネントの再マウントを跨いで状態を維持）
type CodeBlockState = {
  code: string;
  output: string | null;
  error: string | null;
  image: string | null;
  isRunning: boolean;
  hasRun: boolean;
};

const codeBlockStates = new Map<string, CodeBlockState>();
// キーごとのリスナー管理
const keyListeners = new Map<string, Set<() => void>>();

function getCodeBlockState(key: string, initialCode: string): CodeBlockState {
  if (!codeBlockStates.has(key)) {
    codeBlockStates.set(key, {
      code: initialCode,
      output: null,
      error: null,
      image: null,
      isRunning: false,
      hasRun: false,
    });
  }
  return codeBlockStates.get(key)!;
}

export function setCodeBlockState(key: string, updates: Partial<CodeBlockState>) {
  const current = codeBlockStates.get(key);
  if (current) {
    const newState = { ...current, ...updates };
    codeBlockStates.set(key, newState);
    // このキーのリスナーのみに通知
    const listeners = keyListeners.get(key);
    if (listeners) {
      listeners.forEach((l) => l());
    }
  }
}

function createSubscribe(key: string) {
  return (callback: () => void) => {
    if (!keyListeners.has(key)) {
      keyListeners.set(key, new Set());
    }
    keyListeners.get(key)!.add(callback);
    return () => {
      const listeners = keyListeners.get(key);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          keyListeners.delete(key);
        }
      }
    };
  };
}

function useCodeBlockState(key: string, initialCode: string) {
  // subscribe は安定した参照が必要
  const subscribe = useMemo(() => createSubscribe(key), [key]);
  const getSnapshot = useCallback(() => getCodeBlockState(key, initialCode), [key, initialCode]);
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return state;
}

/**
 * RunnableCodeBlock の state key。
 *
 * 以前は initialCode の頭 100 字だけで見分けていたため、頭 100 字が同じで
 * 後ろが違う 2 本のブロックを同じ頁に置くと同じ key になり、片方を実行すると
 * もう片方にも同じ出力が出てしまっていた。
 * 今は initialCode 全体を key に含めるので、内容が 1 字でも違えば別の key になる。
 *
 * それでも「頁の中に内容が完全に同じコードのブロックが 2 つ」ある場合は内容だけ
 * では見分けが付かないので、呼び出し元 (MarkdownContent など、頁の中の並び順を
 * 知っている側) が instanceIndex (描いた順の番号。0 始まり) を渡して区別する。
 * 渡さない呼び出し元は今までどおり 0 (省略時の既定) で、1 頁に 1 本しか置かない
 * 画面 (cs-try, onboarding, HeroCodeRunner など) は挙動が変わらない。
 */
export function getCodeBlockStateKey(
  initialCode: string,
  language: "javascript" | "python" | "typescript" = "javascript",
  instanceIndex = 0,
): string {
  const effective = detectLanguage(initialCode, language);
  return `${effective}-${instanceIndex}-${initialCode}`;
}

/**
 * 画面に出す固定の文言。既定は日本語で、今までの呼び出し元は何も渡さず今までどおり。
 * 英語のページ (/en/visualize など) のように言語を変えたい呼び出し元だけ、
 * 必要な分だけ渡す (部分指定。渡さなかった分は日本語のまま)
 */
export interface RunnableCodeBlockLabels {
  placeholder: string;
  runnableBadge: string;
  reset: string;
  run: string;
  running: string;
  output: string;
  noOutput: string;
  chartAlt: string;
  editCode: string;
  closeEditor: string;
  executeHandlerRequired: string;
}

const DEFAULT_LABELS: RunnableCodeBlockLabels = {
  placeholder: "▶ 実行ボタンを押すと結果がここに表示されます",
  runnableBadge: "実行可能",
  reset: "リセット",
  run: "実行",
  running: "実行中...",
  output: "出力",
  noOutput: "(出力なし)",
  chartAlt: "実行結果のグラフ",
  editCode: "コードを編集",
  closeEditor: "編集を閉じる",
  executeHandlerRequired: "この言語の実行にはonExecuteハンドラーが必要です",
};

export interface RunnableCodeBlockProps {
  /** 初期コード */
  initialCode: string;
  /** 言語 */
  language?: "javascript" | "python" | "typescript";
  /** 高さ (デフォルト: auto) */
  height?: string;
  /** 実行結果のプレースホルダー。labels.placeholder と役目が重なるが、古い呼び出し元のために残す */
  placeholder?: string;
  /** カスタム実行ハンドラー（サーバーアクション経由で実行する場合に使用） */
  onExecute?: (code: string, language: string) => Promise<CodeExecutionResult>;
  /**
   * Monacoを最初の描画ではmountせず、軽量なcode previewを表示する。
   * 長い記事に実行可能blockが多数ある場合に、各blockの「コードを編集」で個別に起動する。
   */
  deferEditorMount?: boolean;
  /**
   * true の間は Monaco を読まず、コードを軽い pre で見せる。切り替えのボタンは出さない。
   * いつ読むかは呼ぶ側が決める (トップのヒーローは触られたとき)。
   */
  holdEditor?: boolean;
  /** 画面の固定文言の上書き。渡した分だけ既定 (日本語) に重なる */
  labels?: Partial<RunnableCodeBlockLabels>;
  /** 編集欄のコードの字の大きさ (px)。既定は 14。字を 15px 以上にそろえる頁 (ゆめさく CS のレッスン) が 15 を渡す */
  fontSize?: number;
  /** 編集欄の行の高さ (px) と、最長の行の右の送りの字数。渡すと Monaco の既定より優先する (ゆめさく CS のレッスン) */
  lineHeight?: number;
  scrollBeyondLastColumn?: number;
  /** 長い行を欄の幅で折り返すか。既定は折り返さない (欄の中で横に送る)。狭い画面で長い行を読ませたい頁が "on" を渡す */
  wordWrap?: "on" | "off";
  /**
   * 頁の中でこのブロックが何番目か (0 始まり)。内容が完全に同じコードのブロックが
   * 1 頁に複数あるとき、initialCode だけでは state の見分けが付かないので、
   * 頁の並び順を知っている呼び出し元 (MarkdownContent 等) が渡す。
   * 渡さなければ 0 (1 頁に 1 本しか置かない画面は今までどおり)
   */
  instanceIndex?: number;
}

// シンプルなJavaScript実行（ブラウザ内で完結）
function executeJavaScript(code: string): { output: string; error: string | null } {
  const logs: string[] = [];
  const originalConsoleLog = console.log;
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;

  // console.logをオーバーライド
  console.log = (...args) => {
    logs.push(args.map((arg) =>
      typeof arg === "object" ? JSON.stringify(arg, null, 2) : String(arg)
    ).join(" "));
  };
  console.error = (...args) => {
    logs.push(`[Error] ${args.map((arg) => String(arg)).join(" ")}`);
  };
  console.warn = (...args) => {
    logs.push(`[Warn] ${args.map((arg) => String(arg)).join(" ")}`);
  };

  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function(code);
    fn();
    return { output: logs.join("\n"), error: null };
  } catch (e) {
    return {
      output: logs.join("\n"),
      error: e instanceof Error ? e.message : String(e),
    };
  } finally {
    console.log = originalConsoleLog;
    console.error = originalConsoleError;
    console.warn = originalConsoleWarn;
  }
}

// 外部API経由でコードを実行
async function executeViaApi(
  code: string,
  language: string,
  apiUrl: string
): Promise<{ output: string; error: string | null }> {
  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        code,
        language,
        // シンプル実行モード（テストケースなし）
        mode: "simple",
      }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => "");
      let errorData: { error?: string } = {};
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || `HTTP error! status: ${response.status}` };
      }
      return {
        output: "",
        error: errorData.error || `HTTP error! status: ${response.status}`,
      };
    }

    const data = await response.json();

    // APIレスポンスの形式に応じて出力を取得
    if (data.output !== undefined) {
      return { output: data.output || "(出力なし)", error: data.error || null };
    }

    // テストケース形式のレスポンスの場合
    if (data.results && Array.isArray(data.results)) {
      const outputs = data.results
        .map((r: any) => r.output || r.stdout || "")
        .filter(Boolean);
      return {
        output: outputs.join("\n") || "(出力なし)",
        error: data.error || null,
      };
    }

    return { output: JSON.stringify(data, null, 2), error: null };
  } catch (e) {
    return {
      output: "",
      error: e instanceof Error ? e.message : String(e),
    };
  }
}

/**
 * コード内容から実際のプログラミング言語を推測する。
 * DB に保存された language タグと実コードが食い違うケース（例: JavaScript タブに Python コード）
 * に備えて、明らかに言語特有の構文がある場合はそちらを優先する。
 */
export function detectLanguage(
  code: string,
  declaredLanguage: "javascript" | "python" | "typescript"
): "javascript" | "python" | "typescript" {
  // Python 特有の構文（f-string、print、def、インデント based if/else、コロン終端）
  const pythonSignals =
    /(^|\n)\s*(print\(|def\s+\w+\(|import\s+\w+|from\s+\w+\s+import|if\s+.+:\s*$|elif\s|else:\s*$|\bTrue\b|\bFalse\b|\bNone\b)|f"|f'|if\s+"[^"]+"\s+in\s+|\s+else\s+"[^"]*"$/m;
  // JavaScript 特有の構文
  const jsSignals =
    /(console\.(log|error|warn)|const\s+\w+\s*=|let\s+\w+\s*=|var\s+\w+\s*=|=>\s*\{|function\s+\w+\s*\()/;

  const looksPython = pythonSignals.test(code);
  const looksJs = jsSignals.test(code);

  // 明確な食い違い: 宣言は JS/TS だが中身は Python
  if ((declaredLanguage === "javascript" || declaredLanguage === "typescript") && looksPython && !looksJs) {
    return "python";
  }
  // 宣言は Python だが中身は JS
  if (declaredLanguage === "python" && looksJs && !looksPython) {
    return "javascript";
  }
  return declaredLanguage;
}

export function RunnableCodeBlock({
  initialCode,
  language = "javascript",
  height = "auto",
  placeholder,
  onExecute,
  deferEditorMount = false,
  holdEditor = false,
  labels: labelsOverride,
  fontSize = 14,
  lineHeight,
  scrollBeyondLastColumn,
  wordWrap = "off",
  instanceIndex = 0,
}: RunnableCodeBlockProps) {
  const labels: RunnableCodeBlockLabels = {
    ...DEFAULT_LABELS,
    ...labelsOverride,
    // placeholder は古い個別 prop のほうを優先する (呼び出し元が今までどおり動くように)
    placeholder: placeholder ?? labelsOverride?.placeholder ?? DEFAULT_LABELS.placeholder,
  };
  const [editorRequested, setEditorRequested] = useState(false);
  const isEditorActive = !holdEditor && (!deferEditorMount || editorRequested);
  const editorRegionId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const isDark = useIsDark(rootRef);

  // コードの中身から実際の言語を推測（DBの言語タグと食い違う場合に備えて）
  const effectiveLanguage = useMemo(
    () => detectLanguage(initialCode, language),
    [initialCode, language]
  );

  // コードをキーとして使用（同じコードブロックを識別）
  const stateKey = getCodeBlockStateKey(initialCode, language, instanceIndex);
  const state = useCodeBlockState(stateKey, initialCode);
  const { code, output, error, image, isRunning, hasRun } = state;


  const setCode = useCallback((newCode: string) => {
    setCodeBlockState(stateKey, { code: newCode });
  }, [stateKey]);

  const handleRun = useCallback(async () => {
    setCodeBlockState(stateKey, { isRunning: true, error: null, output: null, image: null });

    // 少し遅延を入れてUI更新を見せる
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      if (onExecute) {
        const result = await onExecute(code, effectiveLanguage);
        setCodeBlockState(stateKey, {
          output: result.output || labels.noOutput,
          error: result.error,
          image: result.image || null,
          isRunning: false,
          hasRun: true,
        });
      } else if (effectiveLanguage === "javascript") {
        const result = executeJavaScript(code);
        setCodeBlockState(stateKey, {
          output: result.output || labels.noOutput,
          error: result.error,
          isRunning: false,
          hasRun: true,
        });
      } else {
        setCodeBlockState(stateKey, {
          output: "",
          error: labels.executeHandlerRequired,
          isRunning: false,
          hasRun: true,
        });
      }
    } catch (e) {
      setCodeBlockState(stateKey, {
        output: "",
        error: e instanceof Error ? e.message : String(e),
        isRunning: false,
        hasRun: true,
      });
    }
  }, [stateKey, code, effectiveLanguage, onExecute, labels.noOutput, labels.executeHandlerRequired]);

  const handleReset = useCallback(() => {
    setCodeBlockState(stateKey, {
      code: initialCode,
      output: null,
      error: null,
      image: null,
      hasRun: false,
    });
  }, [stateKey, initialCode]);

  // 行数に基づいてエディタの高さを計算
  const lineCount = code.split("\n").length;
  const editorHeight = height !== "auto" ? height : `${Math.max(80, Math.min(lineCount * 22 + 20, 300))}px`;

  return (
    /* Figma: node 43:6335 — CodeContainer */
    <div
      ref={rootRef}
      className={`my-6 rounded-lg border ${RCB.edge} ${RCB.paper} overflow-hidden`}
      data-runnable-code-block="true"
      data-runnable-language={effectiveLanguage}
      data-runnable-has-run={hasRun ? "true" : "false"}
      data-runnable-error={error ? "true" : "false"}
      data-runnable-editor-state={isEditorActive ? "active" : "deferred"}
    >
      {/* ヘッダー: 言語名 + 実行可能バッジ */}
      <div className={`flex items-center justify-between px-4 py-2 ${RCB.band}`}>
        <span className="inline-flex items-center gap-1.5">
          {hasLanguageIcon(effectiveLanguage) && (
            <LanguageIcon language={effectiveLanguage} size={16} />
          )}
          <span className={`font-inter text-[14px] max-sm:text-[15px] max-sm:leading-6 ${RCB.sub} uppercase`}>
            {effectiveLanguage}
          </span>
        </span>
        <span className="inline-flex items-center gap-2">
          {deferEditorMount && (
            <button
              type="button"
              onClick={() => setEditorRequested((current) => !current)}
              aria-controls={editorRegionId}
              aria-expanded={isEditorActive}
              className={`rounded border px-2.5 py-1 font-noto text-[14px] max-sm:text-[15px] max-sm:leading-6 font-bold transition-colors ${RCB.btn} ${
                isEditorActive
                  ? `${RCB.edge} ${RCB.sub}`
                  : `border-[#05B45B]/30 ${RCB.link} hover:bg-[#05B45B]/5`
              }`}
            >
              {isEditorActive ? labels.closeEditor : labels.editCode}
            </button>
          )}
          <span className={`inline-flex items-center px-1.5 py-1 rounded ${RCB.badgeBg}`}>
            <span className={`font-noto font-bold text-[14px] max-sm:text-[15px] max-sm:leading-6 ${RCB.link} tracking-[0.1em]`}>
              {labels.runnableBadge}
            </span>
          </span>
        </span>
      </div>

      {/* エディタ */}
      <div
        id={editorRegionId}
        style={{ height: editorHeight }}
        data-runnable-editor-shell={isEditorActive ? undefined : "true"}
      >
        {isEditorActive ? (
          <Suspense
            fallback={
              <div className={`h-full flex items-center justify-center ${RCB.paper}`}>
                <Loader2 className={`w-5 h-5 animate-spin ${RCB.sub}`} />
              </div>
            }
          >
            <CodeEditor
              value={code}
              language={effectiveLanguage}
              onChange={(value) => setCode(value)}
              darkMode={isDark}
              warmDark
              fontSize={fontSize}
              lineHeight={lineHeight}
              scrollBeyondLastColumn={scrollBeyondLastColumn}
              wordWrap={wordWrap}
            />
          </Suspense>
        ) : (
          <pre
            className={`m-0 h-full overflow-auto ${RCB.paper} p-4 font-mono text-[14px] max-sm:text-[15px] leading-[22px] ${RCB.ink} ${wordWrap === "on" ? "whitespace-pre-wrap break-words" : ""}`}
            tabIndex={0}
            data-runnable-code-preview="true"
          >
            <code>{code}</code>
          </pre>
        )}
      </div>

      {/* ボタン行: リセット(アイコンのみ) + 実行(フル幅) */}
      <div className={`flex items-center gap-4 px-4 py-3 ${RCB.paper}`}>
        <button
          type="button"
          onClick={handleReset}
          disabled={isRunning}
          aria-label={labels.reset}
          title={labels.reset}
          className={`h-10 w-10 flex items-center justify-center rounded-lg border ${RCB.edge} ${RCB.btn} ${RCB.btnHover} disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors`}
        >
          <RotateCcw className={`w-5 h-5 ${RCB.sub}`} />
        </button>
        <button
          type="button"
          onClick={handleRun}
          disabled={isRunning}
          data-runnable-run="true"
          className="flex-1 h-10 flex items-center justify-center gap-2 bg-[#05b45b] hover:bg-[#04a352] active:bg-[#049f50] disabled:hover:bg-[#05b45b] text-white font-noto font-bold text-[14px] max-sm:text-[15px] max-sm:leading-6 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rcb-focus"
        >
          {isRunning ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {labels.running}
            </>
          ) : (
            <>
              <Play
                className="w-5 h-5"
                strokeWidth={2}
                strokeLinejoin="round"
                fill="none"
              />
              {labels.run}
            </>
          )}
        </button>
      </div>

      {/* 出力ラベル */}
      <div className={`flex items-center px-4 py-2.5 ${RCB.band}`}>
        <span className={`font-noto font-bold text-[14px] max-sm:text-[15px] max-sm:leading-6 ${RCB.sub} tracking-[0.1em]`}>
          {labels.output}
        </span>
        {hasRun && !error && (
          <CheckCircle className="w-3.5 h-3.5 text-primary ml-2" />
        )}
        {error && <XCircle className="w-3.5 h-3.5 text-red-500 ml-2" />}
      </div>

      {/* 出力エリア */}
      <div
        className={`px-4 py-3 min-h-[60px] ${RCB.paper}`}
        data-runnable-output="true"
      >
        {!hasRun && (
          <p className={`font-mono text-[14px] max-sm:text-[15px] leading-[1.5] ${RCB.sub}`}>
            {labels.placeholder}
          </p>
        )}
        {hasRun && image && (
          <div className="mb-3">
            <img
              src={image}
              alt={labels.chartAlt}
              className={`max-w-full h-auto rounded-lg border ${RCB.edge}`}
            />
          </div>
        )}
        {/* エラーのときの「(出力なし)」は出さない。読む所がエラーの枠だけになる */}
        {hasRun && output && !(error && output === "(出力なし)") && (
          <pre className={`font-mono text-[14px] max-sm:text-[15px] leading-[1.5] ${RCB.ink} whitespace-pre-wrap`}>
            {output}
          </pre>
        )}
        {error && (
          <div className="mt-2 p-3 rounded-lg border border-rcb-error-edge bg-rcb-error-bg">
            {/* Traceback は行ごとに読ませる (最後の行がエラーの名前と理由) ので、改行を残す */}
            <p className="text-sm max-sm:text-[15px] max-sm:leading-6 font-mono text-rcb-error-ink whitespace-pre-wrap">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// HTMLプレビュー用のコードブロック
export interface HtmlPreviewCodeBlockProps {
  initialCode: string;
  height?: string;
}

export function HtmlPreviewCodeBlock({
  initialCode,
  height = "auto",
}: HtmlPreviewCodeBlockProps) {
  const stateKey = `html-preview-${initialCode.slice(0, 100)}`;
  const state = useCodeBlockState(stateKey, initialCode);
  const { code, hasRun } = state;

  const setCode = useCallback((newCode: string) => {
    setCodeBlockState(stateKey, { code: newCode });
  }, [stateKey]);

  const handleRun = useCallback(() => {
    setCodeBlockState(stateKey, { hasRun: true, output: code });
  }, [stateKey, code]);

  const handleReset = useCallback(() => {
    setCodeBlockState(stateKey, {
      code: initialCode,
      output: null,
      hasRun: false,
    });
  }, [stateKey, initialCode]);

  const lineCount = code.split("\n").length;
  const editorHeight = height !== "auto" ? height : `${Math.max(80, Math.min(lineCount * 22 + 20, 300))}px`;

  return (
    <div className="my-6 rounded-xl border overflow-hidden" style={{ borderColor: "var(--color-editor-border)", backgroundColor: "var(--color-editor-bg)" }}>
      {/* ヘッダー */}
      <div
        className="flex items-center justify-between px-4 py-2"
        style={{
          backgroundColor: "var(--color-editor-sidebar)",
          borderBottom: "1px solid var(--color-editor-border)"
        }}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm max-sm:text-[15px] max-sm:leading-6 font-mono uppercase" style={{ color: "var(--color-editor-lineNumber)" }}>html</span>
          <span className="text-sm max-sm:text-[15px] max-sm:leading-6 text-primary bg-primary/10 px-2 py-0.5 rounded font-bold">実行可能</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            className="flex items-center gap-1 px-2 py-1 text-sm max-sm:text-[15px] max-sm:leading-6 transition-colors hover:opacity-70"
            style={{ color: "var(--color-editor-iconGray)" }}
            title="リセット"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>リセット</span>
          </button>
          <Button
            onClick={handleRun}
            size="sm"
            variant="primary"
          >
            <Play className="w-3.5 h-3.5" />
            表示する
          </Button>
        </div>
      </div>

      {/* エディタ */}
      <div style={{ height: editorHeight }}>
        <Suspense
          fallback={
            <div
              className="h-full flex items-center justify-center"
              style={{ backgroundColor: "var(--color-editor-bg)" }}
            >
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--color-editor-iconGray)" }} />
            </div>
          }
        >
          <CodeEditor
            value={code}
            language="html"
            onChange={(value) => setCode(value)}
            darkMode={false}
            fontSize={14}
          />
        </Suspense>
      </div>

      {/* プレビューエリア */}
      <div style={{ borderTop: "1px solid var(--color-editor-border)" }}>
        <div
          className="flex items-center gap-2 px-4 py-2"
          style={{
            backgroundColor: "var(--color-editor-sidebar)",
            borderBottom: "1px solid var(--color-editor-border)"
          }}
        >
          <span className="text-sm max-sm:text-[15px] max-sm:leading-6 font-bold text-sub">ブラウザプレビュー</span>
          {hasRun && (
            <CheckCircle className="w-3.5 h-3.5 text-primary" />
          )}
        </div>
        <div className="px-4 py-3 min-h-[80px] bg-white">
          {!hasRun ? (
            <p className="text-sm max-sm:text-[15px] max-sm:leading-6 text-sub italic">▶ 「表示する」ボタンを押すと結果がここに表示されます</p>
          ) : (
            <iframe
              srcDoc={`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Hiragino Sans", "Noto Sans CJK JP", sans-serif;
      margin: 0;
      padding: 0;
      line-height: 1.6;
      color: #333;
    }
    h1 { font-size: 2em; font-weight: bold; margin: 0.67em 0; }
    h2 { font-size: 1.5em; font-weight: bold; margin: 0.83em 0; }
    h3 { font-size: 1.17em; font-weight: bold; margin: 1em 0; }
    h4 { font-size: 1em; font-weight: bold; margin: 1.33em 0; }
    h5 { font-size: 0.83em; font-weight: bold; margin: 1.67em 0; }
    h6 { font-size: 0.67em; font-weight: bold; margin: 2.33em 0; }
    p { margin: 1em 0; }
    ul, ol { padding-left: 2em; margin: 1em 0; }
    li { margin: 0.5em 0; }
    a { color: #0066cc; text-decoration: underline; }
    strong, b { font-weight: bold; }
    em, i { font-style: italic; }
    code { background: #f4f4f4; padding: 0.2em 0.4em; border-radius: 3px; font-family: monospace; }
    pre { background: #f4f4f4; padding: 1em; border-radius: 5px; overflow-x: auto; }
    blockquote { border-left: 4px solid #ddd; margin: 1em 0; padding-left: 1em; color: #666; }
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background: #f4f4f4; }
    img { max-width: 100%; height: auto; }
  </style>
</head>
<body>${code}</body>
</html>`}
              className="w-full border-0"
              style={{ minHeight: "100px", height: "auto" }}
              onLoad={(e) => {
                const iframe = e.target as HTMLIFrameElement;
                if (iframe.contentDocument?.body) {
                  iframe.style.height = iframe.contentDocument.body.scrollHeight + "px";
                }
              }}
              sandbox="allow-same-origin"
              title="HTMLプレビュー"
            />
          )}
        </div>
      </div>
    </div>
  );
}
